import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle2, AlertTriangle, Info, X } from "lucide-react";

type ToastKind = "success" | "error" | "info";
interface Toast { id: string; kind: ToastKind; message: string; }

interface Ctx {
  push: (kind: ToastKind, message: string) => void;
}

const ToastCtx = createContext<Ctx>({ push: () => {} });
export function useToast() { return useContext(ToastCtx); }

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [stack, setStack] = useState<Toast[]>([]);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = Math.random().toString(36).slice(2);
    setStack((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => setStack((prev) => prev.filter((t) => t.id !== id)), 4200);
  }, []);

  // A fresh object literal here re-rendered every consumer on each toast and
  // each 4.2s auto-dismiss, which is what made the detail panels refetch and
  // discard whatever was being typed.
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toast-stack">
        {stack.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.kind === "success" && <CheckCircle2 size={18} />}
            {t.kind === "error" && <AlertTriangle size={18} />}
            {t.kind === "info" && <Info size={18} />}
            <div style={{ flex: 1 }}>{t.message}</div>
            <button
              className="icon-btn"
              onClick={() => setStack((prev) => prev.filter((x) => x.id !== t.id))}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
